<template>
  <div>
    <div class="wz-page-header">
      <h1 class="wz-page-title">Gratificaciones</h1>
    </div>

    <wz-table-container>
      <wz-table-toolbar>
        <template #left>
          <div style="min-width: 320px;">
            <wz-select
              label="Empresa"
              v-model="companyId"
              :items="companies"
              defaultOption="Seleccione una empresa"
              @change="loadData"
            />
          </div>
        </template>

        <template #right>
          <wz-tooltip text="Recargar">
            <wz-button variant="outline" @click="loadData()">
              <i class="ri-loop-right-line"></i>
            </wz-button>
          </wz-tooltip>
          <wz-button variant="primary" @click="$router.push({ name: 'gratification-calculate' })">
            <i class="ri-calculator-line mr-1"></i>Calcular gratificación
          </wz-button>
        </template>
      </wz-table-toolbar>

      <wz-table>
        <thead>
          <tr>
            <th>Período</th>
            <th>Año</th>
            <th>Empresa</th>
            <th class="text-center">Total colaboradores</th>
            <th class="text-right">Monto total</th>
            <th class="text-center">Estado</th>
            <th>Fecha de cálculo</th>
            <th class="text-center">Opciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="o in list" :key="o.id">
            <td>{{ periodLabel(o.periodType) }}</td>
            <td>{{ o.periodYear }}</td>
            <td>{{ o.companyName || '-' }}</td>
            <td class="text-center">{{ o.totalEmployees != null ? o.totalEmployees : '-' }}</td>
            <td class="text-right">
              <span v-decimal:pen="o.totalPayable" v-if="o.totalPayable != null"></span>
              <span v-else>-</span>
            </td>
            <td class="text-center">
              <wz-badge :variant="statusVariant(o.status)" pill>{{ statusLabel(o.status) }}</wz-badge>
            </td>
            <td v-local-date="o.calculatedAt"></td>
            <td class="text-center">
              <wz-dropdown :items="rowActions(o)" placement="left" @click="handleAction(o, $event)" />
            </td>
          </tr>
        </tbody>
      </wz-table>

      <wz-table-footer>
        <div class="table-no-results" v-if="!list.length">
          <p class="m-0 px-3">{{ companyId ? 'No se encontraron períodos.' : 'Seleccione una empresa para ver los períodos.' }}</p>
        </div>
      </wz-table-footer>
    </wz-table-container>

    <!-- Modal: confirmar pago -->
    <wz-modal v-model="confirmModal" title="Confirmar pago">
      <p v-if="confirmTarget">
        ¿Confirmar la gratificación
        <strong>{{ periodLabel(confirmTarget.periodType) }} {{ confirmTarget.periodYear }}</strong>?
        Una vez confirmada no se podrá recalcular.
      </p>
      <template #footer>
        <wz-button variant="outline" @click="confirmModal = false">Cancelar</wz-button>
        <wz-button variant="primary" @click="doConfirm">Confirmar pago</wz-button>
      </template>
    </wz-modal>
  </div>
</template>

<script>
import { Alert } from '@wizepe/wizeui'
import { GratificationService, CompanyService } from '@/core/services'
import { Constants } from '@/core/utils'

export default {
  data() {
    return {
      companyId: null,
      companies: [],
      list: [],
      confirmModal: false,
      confirmTarget: null,
    }
  },
  methods: {
    periodLabel(type) {
      const t = Constants.GratificationPeriodType[type]
      return t ? t.label : type
    },
    statusLabel(status) {
      const s = Constants.GratificationStatus[status]
      return s ? s.label : status
    },
    statusVariant(status) {
      const s = Constants.GratificationStatus[status]
      return s ? s.color : 'dark'
    },
    rowActions(o) {
      const actions = [
        { key: 'view',  label: 'Ver detalle' },
        { key: 'excel', label: 'Exportar Excel' },
      ]
      if (o.status === Constants.GratificationStatus.BORRADOR.key) {
        actions.push({ key: 'confirm', label: 'Confirmar pago' })
      }
      return actions
    },
    handleAction(o, item) {
      const key = item && item.key ? item.key : item
      if (key === 'view')    this.$router.push({ name: 'gratification-detail', params: { id: o.id } })
      if (key === 'excel')   this.downloadXlsx(o)
      if (key === 'confirm') this.confirm(o)
    },
    async loadData() {
      if (!this.companyId) { this.list = []; return }
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await GratificationService.getPeriods(this.companyId)
        this.list = resp.payload || []
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al cargar los períodos')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    confirm(o) {
      this.confirmTarget = o
      this.confirmModal = true
    },
    async doConfirm() {
      const o = this.confirmTarget
      if (!o) return
      try {
        this.$store.dispatch('app/loading', true)
        await GratificationService.confirm(o.id)
        Alert.success('Período confirmado correctamente')
        this.confirmModal = false
        await this.loadData()
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al confirmar el período')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async downloadXlsx(o) {
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await GratificationService.downloadXlsx(o.id)
        const url = window.URL.createObjectURL(resp.data)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `gratificaciones-${(o.periodType || '').toLowerCase()}-${o.periodYear}.xlsx`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        console.error(e)
        Alert.error('Error al exportar el Excel')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async loadCompanies() {
      try {
        const resp = await CompanyService.listAll()
        this.companies = (resp.payload || []).map(c => ({ key: c.id, label: c.name }))
      } catch (e) {
        console.error(e)
      }
    },
  },
  async created() {
    await this.loadCompanies()
  },
}
</script>
